import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  ENVIRONMENT_INITIALIZER,
  EnvironmentProviders,
  Injectable,
  NgZone,
  inject,
  makeEnvironmentProviders,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, UnrecoverableStateEvent, VersionEvent } from '@angular/service-worker';
import { TranslocoService } from '@jsverse/transloco';
import { NbComponentStatus, NbToastRef, NbToastrService } from '@nebular/theme';
import { catchError, defaultIfEmpty, interval, map, Observable, of, switchMap, take, timeout } from 'rxjs';

/**
 * ngsw only asks the server whether a newer version exists while the page is loading. Both consumers
 * are all-day data-entry apps, so a tab opened on Monday would still be running Monday's build on
 * Friday. Polling is the only thing that closes that gap.
 */
const UPDATE_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

/** Nebular reads `duration: 0` as "stay on screen until the user clicks it". */
const STAY_UNTIL_CLICKED = 0;

interface NoticeCopyKeys {
  readonly title: string;
  readonly message: string;
}

/**
 * Both consumer apps run in Hungarian by default, so the copy itself cannot live here. These keys are
 * the contract with every app's `assets/i18n/{en,hu}.json`: the same shape in all four files, so an
 * app that is missing a notice shows up as a diff between them.
 */
const UPDATE_READY_COPY: NoticeCopyKeys = {
  title: 'serviceWorker.updateReady.title',
  message: 'serviceWorker.updateReady.message',
};

const BROKEN_CACHE_COPY: NoticeCopyKeys = {
  title: 'serviceWorker.brokenCache.title',
  message: 'serviceWorker.brokenCache.message',
};

/**
 * Shown when the translation cannot be resolved in time.
 *
 * The copy lives in `assets/i18n/*.json`, which neither app's `ngsw-config.json` caches, so reading
 * it is a live network request. The broken-cache notice exists for a tab whose stored copy is
 * already failing, which is exactly when that request is least likely to answer, so the notice must
 * not depend on it. English in a toast is worse than Hungarian; no toast at all is worse than both.
 */
const FALLBACK_COPY: Record<string, { title: string; message: string }> = {
  'serviceWorker.updateReady.title': {
    title: 'Update downloaded',
    message: 'The new version takes effect the next time you reload the page.',
  },
  'serviceWorker.brokenCache.title': {
    title: 'The app copy is damaged',
    message: 'Click here to reload and repair it. Save your work first.',
  },
};

/** How long the copy has to arrive before the notice is shown in English instead. */
const COPY_TIMEOUT_MS = 3000;

/**
 * Watches the ngsw worker for new versions and offers the user a reload, rather than taking one.
 *
 * A silent auto-reload is not an option here: both apps hold long-lived forms and modal windows, and
 * a page that reloads itself under a half-filled form throws the work away. Every path in this
 * service therefore ends in a prompt the user has to click.
 */
@Injectable({ providedIn: 'root' })
export class CheckForUpdateService {
  private readonly updates = inject(SwUpdate);
  private readonly toastr = inject(NbToastrService);
  private readonly transloco = inject(TranslocoService);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  private openNotice: NbToastRef | null = null;

  constructor() {
    // False in dev, and on any load where the worker did not register. There is no worker to talk to
    // then: `versionUpdates` never emits and every `checkForUpdate()` rejects with "Service workers
    // are disabled or not supported by this browser". Subscribe to nothing and start no timer.
    if (!this.updates.isEnabled) {
      return;
    }

    this.updates.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((versionEvent) => {
      this.onVersionEvent(versionEvent);
    });

    this.updates.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((unrecoverableEvent) => {
      this.onUnrecoverableState(unrecoverableEvent);
    });

    this.startPeriodicUpdateCheck();
  }

  private onVersionEvent(versionEvent: VersionEvent): void {
    switch (versionEvent.type) {
      case 'VERSION_READY':
        // Tell, do not act. A toast click is the same gesture as dismissing a toast, so wiring a
        // reload to it means an absent-minded click throws away a half-filled form. Nothing here
        // reloads, activates, or navigates: ngsw serves the new version on the user's own next
        // reload, which is a moment they chose.
        //
        // Deliberately no `activateUpdate()` either. Activating moves this client onto the new
        // version while the tab still runs the old shell, so the next lazy route chunk is looked up
        // in the new manifest under a hash the running app does not know. That is the documented
        // route into the unrecoverable state handled below.
        this.showTranslatedNotice(UPDATE_READY_COPY, 'info').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        break;
      case 'VERSION_INSTALLATION_FAILED':
        // Nothing the user can act on: the tab keeps running the version it already has, and the next
        // check retries the download. It still has to be visible somewhere, because a build that
        // consistently fails to install leaves every client pinned to the previous one.
        console.warn(`Service worker failed to install version ${versionEvent.version.hash}`, versionEvent.error);
        break;
      case 'VERSION_DETECTED':
      case 'NO_NEW_VERSION_DETECTED':
      default:
        // A download that just started, and a check that found nothing, are both invisible by design.
        // `default` also absorbs any event type Angular adds in a later version.
        break;
    }
  }

  private onUnrecoverableState(unrecoverableEvent: UnrecoverableStateEvent): void {
    // The worker can no longer serve the version this tab is running, so assets and lazy chunks are
    // about to start failing. Only a reload recovers it, and there is nothing to activate: the reload
    // goes to the network. This is the one place a click reloads, because the alternative is a tab
    // that is already breaking apart, and there is no working state left to protect.
    console.error('Service worker reached an unrecoverable state', unrecoverableEvent.reason);
    this.showTranslatedNotice(BROKEN_CACHE_COPY, 'danger')
      .pipe(
        switchMap((notice) => notice.onClick().pipe(take(1))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.reload());
  }

  /**
   * Resolves the copy, then shows the notice, which costs one translation load before the toast
   * appears.
   *
   * The standalone `translate()` this lib uses elsewhere cannot be used here. It reads the in-memory
   * translation and hands back the key itself when the active language has not been fetched yet, and
   * nothing fetches it up front: `provideTransloco` only registers the config, TranslocoService's
   * constructor loads nothing, and the loader first runs on a `| transloco` or `*transloco` in a
   * rendered template. In cash-link-crm those sit inside modal forms, so a tab left on a list page
   * has loaded no language at all and the user would read `serviceWorker.updateReady.title` in the
   * toast. `selectTranslate` loads the active language and emits once the copy is really there.
   */
  private showTranslatedNotice(copyKeys: NoticeCopyKeys, status: NbComponentStatus): Observable<NbToastRef> {
    const fallback = FALLBACK_COPY[copyKeys.title];
    return this.transloco.selectTranslate<string[]>([copyKeys.title, copyKeys.message]).pipe(
      // Without this the notice is shown again, in the new language, every time the user switches
      // language: `selectTranslate` follows `langChanges$` for the life of the subscription.
      take(1),
      // Transloco resolves a missing key to the key itself, and on a failed fetch its `handleFailure`
      // returns EMPTY rather than erroring, so the stream can also simply never emit. Both end in the
      // same place: show the notice anyway, in English.
      map(([title, message]) =>
        title === copyKeys.title || message === copyKeys.message
          ? [fallback.title, fallback.message]
          : [title, message],
      ),
      timeout({ first: COPY_TIMEOUT_MS, with: () => of([fallback.title, fallback.message]) }),
      catchError(() => of([fallback.title, fallback.message])),
      defaultIfEmpty([fallback.title, fallback.message]),
      map(([title, message]) => this.showNotice(title, message, status)),
    );
  }

  /**
   * Shows one persistent toast. Clicking it dismisses it, which is what a click on a toast means
   * everywhere else in these apps; callers that need an action attach to `onClick()` themselves.
   *
   * Any notice already on screen is closed first. A tab left open across several deploys would
   * otherwise stack a column of identical toasts, and Nebular's own `preventDuplicates` is not usable
   * for that: on a duplicate its `show()` returns `undefined` while the signature still claims
   * `NbToastRef`, so an `onClick()` on it would throw.
   */
  private showNotice(title: string, message: string, status: NbComponentStatus): NbToastRef {
    // Re-enter the Angular zone before touching the UI. `versionUpdates` is delivered by a
    // `serviceWorker` message listener whose zone depends on where NgswCommChannel happened to be
    // constructed, and the toast is rendered through an overlay that needs change detection to run.
    return this.zone.run(() => {
      this.openNotice?.close();
      const notice = this.toastr.show(message, title, {
        status,
        duration: STAY_UNTIL_CLICKED,
        destroyByClick: true,
      });
      this.openNotice = notice;
      return notice;
    });
  }

  private reload(): void {
    this.document.location.reload();
  }

  private startPeriodicUpdateCheck(): void {
    // The timer has to be created outside the Angular zone. zone.js counts a live `setInterval` as a
    // pending macrotask for as long as it exists, so an in-zone interval leaves NgZone unstable for
    // the entire session: `ApplicationRef.whenStable()` never resolves, and the first thing that
    // waits on it is provideServiceWorker's own default `registerWhenStable` registration.
    //
    // Deliberately not gated on `ApplicationRef.isStable` the way the Angular docs example is. The
    // first tick is six hours out, so there is no bootstrap work to stay clear of, and isStable is a
    // signal these apps cannot be relied on to produce: a single long-lived in-zone macrotask
    // anywhere would mean the poll silently never starts.
    this.zone.runOutsideAngular(() => {
      interval(UPDATE_CHECK_INTERVAL_MS)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.checkForUpdate();
        });
    });
  }

  private checkForUpdate(): void {
    this.updates.checkForUpdate().catch((error: unknown) => {
      // Offline, or the origin is unreachable. Nothing to do but say so: the next tick retries.
      console.warn('Periodic app update check failed', error);
    });
  }
}

/**
 * Registers the update watcher and, the part that actually matters, constructs it.
 *
 * `providedIn: 'root'` only describes how to build the service, and listing the class in a providers
 * array only registers that same recipe again. Neither calls it. That is why this service never ran:
 * both apps listed it and nothing ever injected it. `ENVIRONMENT_INITIALIZER` is Angular 18's hook
 * for "build this at startup"; unlike `APP_INITIALIZER` it is not awaited, so watching for updates
 * cannot delay or block bootstrap.
 */
export function provideCheckForUpdate(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => {
        inject(CheckForUpdateService);
      },
    },
  ]);
}
